import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import oneDark from 'react-syntax-highlighter/dist/esm/styles/prism/one-dark';
import oneLight from 'react-syntax-highlighter/dist/esm/styles/prism/one-light';
import { createContext, useContext, useState, useRef, useEffect } from 'react';
import { Check, Copy, ExternalLink, Play, Film, Square, Mic, ChevronDown, ChevronUp } from 'lucide-react';
import { useIvyChatThemeStore } from '@/stores/ivyChatThemeStore';
import { RefreshableImage } from '@/components/ui/refreshable-image';
import { parseCheckpointBlock } from '@/lib/checkpointBlock';
import { CheckpointCard } from './CheckpointCard';
import { VoiceCardB } from './VoiceCardB';
import { studioBOn } from '@/lib/studioB/flag';
import { useVoiceCatalog, voiceLine } from '@/hooks/useVoiceCatalog';
import { useVoiceAnswer } from '@/lib/ivyChat/voiceAnswer';

const ALLOWED_IFRAME_HOSTS = [
  'app.4klabs.ai',
  'www.youtube.com',
  'youtube.com',
  'player.vimeo.com',
];

function isAllowedIframeSrc(src: string): boolean {
  try {
    const url = new URL(src);
    return ALLOWED_IFRAME_HOSTS.some(h => url.hostname === h || url.hostname.endsWith('.' + h));
  } catch {
    return false;
  }
}

interface Props {
  content: string;
  /** Which state an `ivy-checkpoint` card in this content is in; see CheckpointCard. */
  checkpoint?: { active: boolean; stale: boolean; answer?: string };
}

const CheckpointContext = createContext<Props['checkpoint']>(undefined);

export function IvyMarkdownContent({ content, checkpoint }: Props) {
  return (
    <CheckpointContext.Provider value={checkpoint}>
      <ReactMarkdown remarkPlugins={REMARK} rehypePlugins={REHYPE} components={RENDERERS}>
        {preprocessVideoLinks(content)}
      </ReactMarkdown>
    </CheckpointContext.Provider>
  );
}

function MarkdownCode({ className, children }: { className?: string; children?: React.ReactNode }) {
  const checkpoint = useContext(CheckpointContext);
  const match = /language-(\w+[-\w]*)/.exec(className || '');
  const isInline = !match;
  if (isInline) {
    return <code className={className}>{children}</code>;
  }
  const language = match[1];
  // Intercept voice selector code blocks
  if (language === 'ivy-voice-selector' || language === 'ivy') {
    try {
      const config = JSON.parse(String(children).trim());
      if (config.recommended && config.candidates) {
        return studioBOn() ? <VoiceCardB config={config} /> : <InlineVoiceSelector config={config} />;
      }
    } catch { /* not voice JSON, render as code block */ }
  }
  if (language === 'ivy-checkpoint') {
    const cp = parseCheckpointBlock(String(children).trim());
    if (cp) return <CheckpointCard checkpoint={cp} active={checkpoint?.active ?? false} stale={checkpoint?.stale ?? true} answer={checkpoint?.answer} />;
  }
  return (
    <CodeBlock language={language}>
      {String(children).replace(/\n$/, '')}
    </CodeBlock>
  );
}

const REMARK = [remarkGfm];
const REHYPE = [rehypeRaw];

// One set for every render: a new set each render is a new component type per element, so every redraw of a message
// (one per streamed event) would rebuild its cards and drop what they hold, such as an answered voice.
const RENDERERS: Components = {
  iframe: (props) => {
    const src = String(props.src || '');
    if (!isAllowedIframeSrc(src)) {
      return (
        <a href={src} target="_blank" rel="noopener noreferrer" className="text-info underline text-sm">
          {src}
        </a>
      );
    }
    return <VideoEmbed src={src} />;
  },

  img: ({ src, alt }) => {
    if (src) {
      return <RefreshableImage src={src} alt={alt || ''} className="max-w-full h-auto rounded-lg shadow-sm my-2" />;
    }
    return null;
  },

  a: ({ href, children }) => {
    if (href && isVideoUrl(href)) {
      return <VideoLinkCard url={href} label={String(children)} />;
    }
    return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
  },

  // Direction B (beta only): a card drawn from an ivy-* block is not a code block, so no <pre> around it.
  pre({ node, children, ...rest }) {
    const code = node?.children?.[0];
    const lang = code && 'properties' in code ? String((code.properties?.className as string[] | undefined)?.[0] ?? '') : '';
    if (studioBOn() && /^language-ivy(-|$)/.test(lang)) return <>{children}</>;
    return <pre {...rest}>{children}</pre>;
  },

  code: MarkdownCode,
};

function isVideoUrl(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.hostname === 'play.4klabs.ai') return /^\/[A-Za-z0-9_-]+\/?$/.test(u.pathname);
    return u.hostname === 'app.4klabs.ai' && (u.pathname.startsWith('/video/') || u.pathname.startsWith('/embed/'));
  } catch {
    return false;
  }
}

function getEmbedUrl(url: string): string {
  try {
    const u = new URL(url);
    if (u.pathname.startsWith('/video/')) {
      const id = u.pathname.replace('/video/', '');
      return `https://app.4klabs.ai/embed/${id}`;
    }
    if (u.pathname.startsWith('/embed/')) {
      return url;
    }
  } catch { /* fall through */ }
  return url;
}

function preprocessVideoLinks(content: string): string {
  if (content.includes('<iframe')) return content;

  return content.replace(
    // Not an address that is already a markdown link's target: that link keeps its own words.
    /(?<!\]\()(?:Share:\s*)?(https:\/\/(?:app\.4klabs\.ai\/(?:video|embed)\/[A-Za-z0-9_-]+|play\.4klabs\.ai\/[A-Za-z0-9_-]+\/?))/g,
    (_match, url) => {
      return `[Open Video](${url})`;
    }
  );
}

function VideoEmbed({ src }: { src: string }) {
  const embedUrl = getEmbedUrl(src);
  const shareUrl = embedUrl.replace('/embed/', '/video/');

  if (studioBOn()) {
    return (
      <div className="my-3 overflow-hidden rounded-[var(--b-radius-card)] border border-b-line bg-b-elevated">
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe src={embedUrl} className="absolute inset-0 h-full w-full" frameBorder="0" allowFullScreen allow="autoplay 'src' https://play.4klabs.ai; fullscreen 'src' https://play.4klabs.ai" />
        </div>
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <span className="text-b-meta text-b-t3">Your video</span>
          <div className="flex items-center gap-1.5">
            <CopyUrlButton url={shareUrl} />
            <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-8 items-center gap-1.5 rounded-[var(--b-radius-control)] bg-b-ink px-3 text-b-control text-b-on-ink no-underline hover:opacity-90">
              <ExternalLink className="h-3.5 w-3.5" />
              Open
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-4 rounded-xl overflow-hidden border border-border bg-surface-elevated shadow-md">
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={embedUrl}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allowFullScreen
          allow="autoplay 'src' https://play.4klabs.ai; fullscreen 'src' https://play.4klabs.ai"
        />
      </div>
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-border-secondary">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Film className="w-3.5 h-3.5" />
          <span>Ivy Video</span>
        </div>
        <div className="flex items-center gap-2">
          <CopyUrlButton url={shareUrl} />
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            Open
          </a>
        </div>
      </div>
    </div>
  );
}

function VideoLinkCard({ url, label }: { url: string; label: string }) {
  const embedUrl = getEmbedUrl(url);
  const [showEmbed, setShowEmbed] = useState(false);

  if (showEmbed) {
    return <VideoEmbed src={embedUrl} />;
  }

  if (studioBOn()) {
    return (
      <button
        type="button"
        data-video-link
        onClick={() => setShowEmbed(true)}
        className="group my-2 flex w-full max-w-md items-center gap-3 rounded-[var(--b-radius-card)] border border-b-line bg-b-raised px-3 py-2.5 text-left transition-colors hover:bg-b-elevated"
      >
        <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-b-ink text-b-on-ink">
          <Play className="ml-px h-4 w-4 fill-current" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-b-read font-medium text-b-t1">{label || 'Play the video'}</span>
          <span className="block truncate text-b-meta text-b-t3">{url.replace(/^https:\/\//, '')}</span>
        </span>
        <ExternalLink className="h-4 w-4 flex-none text-b-t3 group-hover:text-b-t1" />
      </button>
    );
  }

  return (
    <button
      data-video-link
      onClick={() => setShowEmbed(true)}
      className="inline-flex items-center gap-2 my-2 px-4 py-3 rounded-xl border border-border bg-surface hover:bg-muted/50 transition-colors text-left w-full max-w-md group"
    >
      <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand/20 transition-colors">
        <Play className="w-5 h-5 text-brand" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground">{label || 'Play Video'}</div>
        <div className="text-xs text-muted-foreground truncate">{url}</div>
      </div>
      <ExternalLink className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors flex-shrink-0" />
    </button>
  );
}

function CopyUrlButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
    >
      {copied ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy link'}
    </button>
  );
}

// ── Inline Voice Selector ────────────────────────────────────────────────

interface VoiceSelectorConfig {
  recommended: string;
  context: string;
  candidates: string[];
}

function InlineVoiceSelector({ config }: { config: VoiceSelectorConfig }) {
  const [answer, pick] = useVoiceAnswer(config.recommended);
  const [choice, setSelected] = useState(config.recommended);
  const selected = answer ?? choice;
  const submitted = answer !== null;
  const [playing, setPlaying] = useState<string | null>(null);
  const [showMore, setShowMore] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const catalog = useVoiceCatalog();

  // Split candidates: recommended first, then other candidates, then the rest of the voice list
  const recommended = config.recommended;
  const otherCandidates = config.candidates.filter(n => n !== recommended);
  const extraVoices = [...(catalog?.keys() ?? [])].filter(n => n !== recommended && !otherCandidates.includes(n));

  const handlePlay = (name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (playing === name) {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlaying(null);
      return;
    }
    const sampleUrl = catalog?.get(name)?.sampleUrl;
    if (!sampleUrl) return;
    audioRef.current?.pause();
    const audio = new Audio(sampleUrl);
    audioRef.current = audio;
    audio.addEventListener('ended', () => setPlaying(null));
    audio.addEventListener('error', () => setPlaying(null));
    audio.play().catch(() => setPlaying(null));
    setPlaying(name);
  };

  useEffect(() => {
    return () => { audioRef.current?.pause(); };
  }, []);

  const handleConfirm = () => pick(selected);

  const VoiceRow = ({ name, badge }: { name: string; badge?: string }) => {
    const meta = catalog?.get(name);
    const isSelected = name === selected;
    const isPlaying = playing === name;

    return (
      <button
        key={name}
        disabled={submitted}
        title={meta?.persona ? `${meta.persona}. ${meta.summary ?? ''}` : undefined}
        onClick={() => setSelected(name)}
        className={`w-full flex items-center gap-2 px-3 py-1.5 transition-colors text-left disabled:opacity-60 ${
          isSelected ? 'bg-brand/5' : 'hover:bg-muted/30'
        }`}
      >
        <button
          onClick={(e) => handlePlay(name, e)}
          disabled={submitted}
          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
            isPlaying
              ? 'bg-brand text-white'
              : 'bg-muted hover:bg-muted-foreground/20'
          }`}
        >
          {isPlaying ? (
            <Square className="w-2 h-2 fill-current" />
          ) : (
            <Play className="w-2.5 h-2.5 fill-current ml-px" />
          )}
        </button>

        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          <span className="text-xs font-medium text-foreground truncate">{name}</span>
          {meta && (
            <span className="text-[10px] text-muted-foreground">{voiceLine(meta)}</span>
          )}
          {meta && (
            <span className="text-[9px] px-1 py-px rounded-full bg-muted text-muted-foreground">
              {meta.gender === 'female' ? '♀' : '♂'}
            </span>
          )}
          {badge && (
            <span className="text-[9px] px-1.5 py-px rounded-full bg-success/10 text-success font-medium">
              {badge}
            </span>
          )}
        </div>

        <div className={`w-4 h-4 rounded-full border-[1.5px] flex-shrink-0 flex items-center justify-center transition-colors ${
          isSelected ? 'border-brand bg-brand' : 'border-muted-foreground/30'
        }`}>
          {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
        </div>
      </button>
    );
  };

  return (
    <div className="my-2 rounded-lg border border-border overflow-hidden bg-surface">
      {/* Header */}
      <div className="flex items-start gap-2 px-3 py-2 bg-surface-elevated border-b border-border">
        <Mic className="w-3.5 h-3.5 text-brand mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-medium text-foreground">Pick a voice</span>
          {config.context && (
            <p className="text-[11px] text-muted-foreground leading-snug mt-0.5 break-words">
              {config.context}
            </p>
          )}
        </div>
      </div>

      {/* Recommended voice */}
      <div className="border-b border-border/30">
        <VoiceRow name={recommended} badge="Best fit" />
      </div>

      {/* Other recommended candidates */}
      {otherCandidates.length > 0 && (
        <div className="divide-y divide-border/20">
          {otherCandidates.map(name => (
            <VoiceRow key={name} name={name} />
          ))}
        </div>
      )}

      {/* Expandable: more voices */}
      {extraVoices.length > 0 && (
        <>
          <button
            onClick={() => setShowMore(!showMore)}
            className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-[11px] text-muted-foreground hover:text-foreground border-t border-border/30 hover:bg-muted/20 transition-colors"
          >
            {showMore ? (
              <>Show less <ChevronUp className="w-3 h-3" /></>
            ) : (
              <>More voices ({extraVoices.length}) <ChevronDown className="w-3 h-3" /></>
            )}
          </button>
          {showMore && (
            <div className="divide-y divide-border/20 border-t border-border/30">
              {extraVoices.map(name => (
                <VoiceRow key={name} name={name} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Confirm button */}
      <div className="px-3 py-2 border-t border-border">
        <button
          onClick={handleConfirm}
          disabled={submitted}
          className={`w-full py-1.5 rounded-md text-xs font-medium transition-all ${
            submitted
              ? 'bg-success/10 text-success cursor-default'
              : 'bg-brand hover:bg-brand/90 text-white'
          }`}
        >
          {submitted ? `✓ Using ${selected}` : `Use ${selected}`}
        </button>
      </div>
    </div>
  );
}

// ── Code Block ───────────────────────────────────────────────────────────

function CodeBlock({ language, children }: { language: string; children: string }) {
  const [copied, setCopied] = useState(false);
  const resolved = useIvyChatThemeStore(s => s.resolved);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group rounded-lg overflow-hidden my-3 border border-border">
      <div className="flex items-center justify-between px-3 py-1.5 bg-surface-elevated border-b border-border">
        <span className="text-xs text-muted-foreground font-mono">{language}</span>
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-muted text-muted-foreground/40 hover:text-foreground transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <SyntaxHighlighter
        style={resolved === 'dark' ? oneDark : oneLight}
        language={language}
        PreTag="div"
        customStyle={{
          margin: 0,
          borderRadius: 0,
          fontSize: '12px',
          lineHeight: '1.5',
          background: 'transparent',
        }}
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
}
